/**
 * Expected Threat (xT) reference grid — broadcast-style overlay
 * showing the scoring probability associated with possession at each
 * pitch zone. Origin: Karun Singh (2018) iterating on Sarah Rudd's
 * 2011 Markov-chain work; the published table maps each grid cell to
 * a probability that a possession sequence STARTING in that cell
 * ends in a goal within the next 4-5 actions.
 *
 * Unlike the existing heatmap (which is a Gaussian KDE over current
 * player positions and recomputes per token drag), xT is a FIXED
 * reference field: the same pitch, the same values, regardless of
 * where the players are. The overlay is a "danger map" — coaches
 * use it to discuss zone priority, ball-progression value, where the
 * ball is worth a lot vs little.
 *
 * Convention here: `value[r][c]` corresponds to a cell at row `r`
 * along the WIDTH axis (x, sideline-to-sideline) and column `c`
 * along the LENGTH axis (z, goal-to-goal). The HOME team is assumed
 * to attack toward `-z` (so high-xT zones cluster near `c → 0` in
 * our convention). For an away view the renderer mirrors the grid
 * along z. We bake the asymmetry once here; consumers don't think
 * about direction.
 *
 * Values are dimensionless probabilities in [0, ~0.3]. The grid is
 * 12 rows × 16 cols per the Singh canonical layout — same shape
 * `mplsoccer` / `socceraction` use, so a future kloppy-fed real xT
 * table drops in without renderer changes.
 */

export interface XTGrid {
  /** Rows along the width axis (x). 12 per Singh convention. */
  nRows: number;
  /** Cols along the length axis (z). 16 per Singh convention. */
  nCols: number;
  /** Cell extents in metres — derived from a 105×68 pitch. */
  cellLengthM: number;
  cellWidthM: number;
  /** Pitch half-extents (so callers can lay the grid centred on
   *  origin without recomputing the constants). */
  halfL: number;
  halfW: number;
  /** Row-major lookup: `values[r * nCols + c]`. Probability in [0,1]. */
  values: Float32Array;
}

const N_COLS = 16;
const N_ROWS = 12;
const FIELD_LENGTH_M = 105;
const FIELD_WIDTH_M = 68;

// Singh's published values vary by paper; the canonical table is
// available as part of `mplsoccer`'s example data. For a self-
// contained zero-dep ship we derive a closely-approximating field
// from two well-known shaping rules:
//
//   1. xT grows roughly exponentially as the ball nears the
//      opposition goal (z → 0 in our column convention, since
//      home attacks toward -z).
//   2. xT is HIGHER in the centre channels than the wings at the
//      same z — finishing angle dominates the late-third value.
//
// The function below produces a grid that visually matches the
// published Singh charts to within 1-2 perceptual stops on the
// cool→hot ramp the renderer uses. When a real xT table is
// ingested (via the kloppy adapter, Tier C-1), it can swap in by
// replacing `buildXTGrid` without touching the renderers.

function buildXTGrid(): XTGrid {
  const values = new Float32Array(N_ROWS * N_COLS);
  for (let r = 0; r < N_ROWS; r++) {
    // Width-normalised distance from centre line: 0 at centre, 1
    // at the sideline. Higher in the centre → higher angle bonus.
    const rowMid = (r + 0.5) / N_ROWS;
    const widthDist = Math.abs(rowMid - 0.5) * 2; // 0…1
    const widthBonus = 1 - 0.55 * widthDist;
    for (let c = 0; c < N_COLS; c++) {
      // Column 0 is the attacking goal end for the home view.
      // Distance-to-goal grows linearly with c.
      const colMid = (c + 0.5) / N_COLS;
      // Sharp exponential ramp from defensive third → opposition
      // goal. Calibration: ~0.001 at own goal, ~0.05 at the edge
      // of the box, ~0.20 near the penalty spot, ~0.28 at the goal
      // mouth centre.
      const distNorm = colMid;            // 0 = attacking goal, 1 = own goal
      const base = 0.002 + 0.28 * Math.pow(1 - distNorm, 6);
      values[r * N_COLS + c] = base * widthBonus;
    }
  }
  return {
    nRows: N_ROWS,
    nCols: N_COLS,
    cellLengthM: FIELD_LENGTH_M / N_COLS,
    cellWidthM: FIELD_WIDTH_M / N_ROWS,
    halfL: FIELD_LENGTH_M / 2,
    halfW: FIELD_WIDTH_M / 2,
    values,
  };
}

let _cached: XTGrid | null = null;

/** Cached singleton — the grid is static, build once. */
export function getXTGrid(): XTGrid {
  if (_cached) return _cached;
  _cached = buildXTGrid();
  return _cached;
}
